import { z } from 'zod'
import axios from 'axios'
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { PlayerImageSchema } from '../players/api'
import { TeamLogoSchema } from '../assets/api'

type LearningStatsParams = {
  player_id?: number
  team_id?: number
}

async function getLearningStats(params?: LearningStatsParams) {
  const response = await axios.get('/api/learning/stats', { params })
  return LearningStatsSchema.parse(response.data)
}

export function useLearningStats(params?: LearningStatsParams) {
  return useSuspenseQuery({
    queryKey: ['learningStats', params],
    queryFn: () => getLearningStats(params),
  })
}

export function useLearningStatsOptional(
  params: LearningStatsParams & { enabled: boolean }
) {
  const { enabled, ...queryParams } = params
  return useQuery({
    queryKey: ['learningStats', queryParams],
    queryFn: () => getLearningStats(queryParams),
    enabled,
  })
}

async function getCourses() {
  const response = await axios.get('/api/learning/quizzes')
  return z.array(CourseSchema).parse(response.data)
}

export function useCourses() {
  return useSuspenseQuery({
    queryKey: ['courses'],
    queryFn: getCourses,
  })
}

async function getTeamsLearningRanks({
  competition_id,
}: {
  competition_id: number
}) {
  const response = await axios.get(
    `/api/competitions/${competition_id}/learning/ranks/teams`
  )
  return z.array(TeamLearningSchema).parse(response.data)
}

export function useTeamsLearningRanks({
  competition_id,
}: {
  competition_id: number
}) {
  return useSuspenseQuery({
    queryKey: ['teamsLearningRanks', { competition_id }],
    queryFn: () => getTeamsLearningRanks({ competition_id }),
  })
}

async function getPlayersLearningRanks({
  competition_id,
}: {
  competition_id: number
}) {
  const response = await axios.get(
    `/api/competitions/${competition_id}/learning/ranks/players`
  )
  return z.array(PlayerLearningSchema).parse(response.data)
}

export function usePlayersLearningRanks({
  competition_id,
}: {
  competition_id: number
}) {
  return useSuspenseQuery({
    queryKey: ['playersLearningRanks', { competition_id }],
    queryFn: () => getPlayersLearningRanks({ competition_id }),
  })
}

async function getCourseDetails({ course_id }: { course_id: number }) {
  const response = await axios.get(`/api/learning/quizzes/${course_id}`)
  return CourseDetailsSchema.parse(response.data)
}

export function useCourseDetails({ course_id }: { course_id: number }) {
  return useSuspenseQuery({
    queryKey: ['courseDetails', { course_id }],
    queryFn: () => getCourseDetails({ course_id }),
    staleTime: 0,
  })
}

async function startAttempt(quiz_id: number) {
  const response = await axios.post(`/api/learning/quizzes/${quiz_id}/attempt`)
  return QuizAttemptSchema.parse(response.data)
}

export function useStartAttempt() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startAttempt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] })
      queryClient.invalidateQueries({ queryKey: ['courseDetails'] })
      queryClient.invalidateQueries({ queryKey: ['learningStats'] })
    },
  })
}

async function submitAnswer({
  attempt_id,
  question_id,
  selected_answer_ids,
}: {
  attempt_id: number
  question_id: number
  selected_answer_ids: number[]
}) {
  const response = await axios.post(
    `/api/learning/attempts/${attempt_id}/answer`,
    { question_id, selected_answer_ids }
  )
  return AnswerFeedbackSchema.parse(response.data)
}

export function useSubmitAnswer() {
  return useMutation({
    mutationFn: submitAnswer,
  })
}

async function finishAttempt(attempt_id: number) {
  const response = await axios.post(
    `/api/learning/attempts/${attempt_id}/finish`
  )
  return FinishAttemptResponseSchema.parse(response.data)
}

export function useFinishAttempt(course_id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: finishAttempt,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['courses'] })
      queryClient.refetchQueries({ queryKey: ['learningStats'] })
      queryClient.refetchQueries({
        queryKey: ['courseDetails', { course_id }],
      })
    },
  })
}

export async function getAttemptResult(attempt_id: number) {
  const response = await axios.get(
    `/api/learning/attempts/${attempt_id}/result`
  )
  return QuizAttemptSchema.parse(response.data)
}

async function getAttemptQuestions(attempt_id: number) {
  const response = await axios.get(
    `/api/learning/attempts/${attempt_id}/questions`
  )
  return AttemptQuestionsResponseSchema.parse(response.data)
}

export function useAttemptQuestions(attempt_id: number) {
  return useSuspenseQuery({
    queryKey: ['attemptQuestions', { attempt_id }],
    queryFn: () => getAttemptQuestions(attempt_id),
  })
}

export function useAttemptResult(attempt_id: number | undefined) {
  return useQuery({
    queryKey: ['attemptResult', { attempt_id }],
    queryFn: () => getAttemptResult(attempt_id!),
    enabled: !!attempt_id,
    refetchInterval: (query) => {
      // Poll every 5 seconds if attempt is not yet completed
      const data = query.state.data
      if (data && !data.completed_at) {
        return 5000
      }
      return false
    },
  })
}

const LearningStatsSchema = z.object({
  rank: z.number().nonnegative(),
  xp_points: z.number().nonnegative(),
  xp_rating: z.number().nonnegative(),
  xp_level: z.enum(['Platinum', 'Gold', 'Silver', 'Bronze', 'Nearly there']),
  completed_courses: z.number(),
  in_progress_courses: z.number(),
  not_started_courses: z.number(),
})

const InProgressAttemptSchema = z.object({
  attempt_id: z.number().positive(),
  answered_question_ids: z.array(z.number().positive()),
  created_at: z.string().datetime(),
})

const BasicCourseSchema = z.object({
  quiz_id: z.number().int().positive(),
  title: z.string(),
  description: z.string(),
  is_mandatory: z.boolean(),
  is_published: z.boolean(),
  publish_at: z.string().datetime().nullable(),
  soft_deadline: z.string().datetime().nullable(),
  hard_deadline: z.string().datetime().nullable(),
  soft_deadline_penalty: z.number().int().nonnegative(),
  is_active: z.boolean(),
  duration_seconds: z.number().int().nonnegative().nullable(),
  quiz_retry_attempts: z.number().int().nonnegative().nullable(),
  question_retry_attempts: z.number().int().nonnegative().nullable(),
  show_correct_answers: z.boolean(),
  pass_mark: z.string(),
  learning_xp: z.number().int().nonnegative(),
  created_at: z.string().datetime(),
  in_progress_attempt: InProgressAttemptSchema.nullable(),
})

const CourseSchema = BasicCourseSchema.extend({
  status: z.enum(['not_started', 'completed', 'in_progress', 'failed']),
  xp_earned: z.number().nonnegative().nullable(),
  completed_at: z.string().datetime().nullable(),
  attempts_count: z.number().nonnegative(),
})

const SlideSchema = z.object({
  slide_id: z.number().positive(),
  order: z.number().nonnegative(),
  title: z.string(),
  image_id: z.number().positive(),
})

const VideoSchema = z.object({
  video_id: z.number().positive(),
  order: z.number().nonnegative(),
  title: z.string(),
  asset_id: z.number().positive(),
})

const AnswerSchema = z.object({
  answer_id: z.number().positive(),
  question_id: z.number().positive(),
  order: z.number().nonnegative(),
  answer_text: z.string(),
})

const QuestionSchema = z.object({
  question_id: z.number().positive(),
  question_group_id: z.number().positive(),
  order: z.number().nonnegative(),
  question_text: z.string(),
  hint_text: z.string(),
  pick_count: z.number().int().positive(),
  correct_points: z.string(),
  incorrect_points: z.string(),
  answers: z.array(AnswerSchema),
})

const QuestionGroupSchema = z.object({
  question_group_id: z.number().positive(),
  title: z.string(),
  shuffle_questions: z.boolean(),
  shuffle_answers: z.boolean(),
  order: z.number().nonnegative(),
  questions: z.array(QuestionSchema),
})

const AttemptAnswerSchema = z.object({
  answer_id: z.number().positive(),
  question_id: z.number().positive(),
  order: z.number().nonnegative(),
  answer_text: z.string(),
})

const AttemptQuestionSchema = z.object({
  question_id: z.number().positive(),
  question_text: z.string(),
  hint_text: z.string(),
  pick_count: z.number().int().positive(),
  correct_points: z.string(),
  incorrect_points: z.string(),
  answers: z.array(AttemptAnswerSchema),
})

const AttemptQuestionGroupSchema = z.object({
  question_group_id: z.number().positive(),
  title: z.string(),
  order: z.number().nonnegative(),
  questions: z.array(AttemptQuestionSchema),
})

const AttemptQuestionsResponseSchema = z.object({
  question_groups: z.array(AttemptQuestionGroupSchema),
})

const CourseDetailsSchema = BasicCourseSchema.extend({
  status: z.enum(['not_started', 'completed', 'in_progress', 'failed']),
  attempts_count: z.number().int().nonnegative(),
  slides: z.array(SlideSchema),
  videos: z.array(VideoSchema),
  question_groups: z.array(QuestionGroupSchema),
})

const QuizAttemptSchema = z.object({
  attempt_id: z.number().positive(),
  quiz_id: z.number().positive(),
  user_id: z.number().positive(),
  score: z.string(),
  percent: z.string(),
  passed: z.boolean(),
  learning_xp: z.number().int().nonnegative(),
  created_at: z.string().datetime(),
  completed_at: z.string().datetime().nullable(),
})

const AnswerFeedbackSchema = z.object({
  is_correct: z.boolean(),
  correct_answer_ids: z.array(z.number().positive()),
  points_awarded: z.string(),
})

const FinishAttemptResponseSchema = z.object({
  attempt_id: z.number().positive(),
  score: z.string(),
  percent: z.string(),
  passed: z.boolean(),
  learning_xp: z.number().int().nonnegative(),
})

const TeamLearningSchema = z.object({
  team_id: z.number().positive(),
  team_name: z.string(),
  team_logo: TeamLogoSchema,
  rank: z.number().positive(),
  xp_points: z.number().nonnegative(),
  xp_rating: z.number().nonnegative(),
  xp_level: z.string(),
  completed_percentage: z.number().nonnegative(),
})

const PlayerLearningSchema = TeamLearningSchema.extend({
  player_id: z.number().positive(),
  player_name: z.string(),
  player_photo: PlayerImageSchema,
})

export type LearningStats = z.infer<typeof LearningStatsSchema>
export type Course = z.infer<typeof CourseSchema>
export type CourseDetails = z.infer<typeof CourseDetailsSchema>
export type QuizAttempt = z.infer<typeof QuizAttemptSchema>
export type AnswerFeedback = z.infer<typeof AnswerFeedbackSchema>
export type FinishAttemptResponse = z.infer<typeof FinishAttemptResponseSchema>
export type QuestionGroup = z.infer<typeof QuestionGroupSchema>
export type Question = z.infer<typeof QuestionSchema>
export type Answer = z.infer<typeof AnswerSchema>
export type Team = z.infer<typeof TeamLearningSchema>
export type Player = z.infer<typeof PlayerLearningSchema>
export type InProgressAttempt = z.infer<typeof InProgressAttemptSchema>
export type AttemptQuestionsResponse = z.infer<
  typeof AttemptQuestionsResponseSchema
>
export type AttemptQuestionGroup = z.infer<typeof AttemptQuestionGroupSchema>
export type AttemptQuestion = z.infer<typeof AttemptQuestionSchema>
